import { createStyles, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Dot,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useCustomerCreationRate } from '@portals/api/partners';

export function CustomersCreationWidget() {
  const { classes, theme } = useStyles();

  const customerCreationRate = useCustomerCreationRate();

  const chartData = useMemo(() => {
    if (!customerCreationRate.data) return [];

    return Object.entries(customerCreationRate.data?.data || {})?.map(
      ([key, value]) => {
        return {
          timestamp: key,
          customers: value,
        };
      }
    );
  }, [customerCreationRate.data]);

  return (
    <Stack spacing="xl" className={classes.chartContainer}>
      <Text size="md" color="gray.6">
        Customers count over time
      </Text>

      <ResponsiveContainer width="100%" minHeight={100}>
        <LineChart
          data={chartData}
          margin={{ left: -35, right: 0, bottom: 10, top: 10 }}
        >
          <CartesianGrid stroke={theme.colors.gray[2]} strokeDasharray="3 3" />

          <XAxis
            dataKey="timestamp"
            tickFormatter={(value) => dayjs(value).format('DD/MM/YY')}
            tickMargin={10}
            tickSize={5}
            tickLine={{
              strokeWidth: 1,
              stroke: theme.colors.gray[2],
            }}
            axisLine={{
              strokeWidth: 1,
              stroke: theme.colors.gray[2],
            }}
          />

          <YAxis
            dataKey="customers"
            type="number"
            allowDataOverflow
            tickLine={{
              strokeWidth: 1,
              stroke: theme.colors.gray[2],
            }}
            tick={{
              fill: theme.colors.gray[4],
              fontSize: theme.fontSizes.xs,
            }}
            axisLine={{
              strokeWidth: 1,
              stroke: theme.colors.gray[2],
            }}
          />

          <Tooltip />

          <Legend
            iconType="square"
            iconSize={10}
            layout="horizontal"
            align="left"
            wrapperStyle={{
              bottom: -10,
              left: 8,
            }}
          />

          <Line
            isAnimationActive={false}
            dataKey="customers"
            strokeWidth={2}
            dot={false}
            activeDot={(props) => (
              <Dot
                {...props}
                fill={theme.primaryColor}
                r={6}
                stroke={theme.fn.themeColor(theme.white)}
                strokeWidth={0}
              />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  chartContainer: {
    backgroundColor: theme.white,
    borderRadius: 20,
    padding: theme.spacing.xxl,
  },
}));
